import { AIScribeRecordingService } from '@/lib/ai-scribe-recording';
import { AiScribeCompleteResponse } from '@/lib/ai-scribe-service';
import { AudioProcessorService } from '@/lib/audio-processor';
import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';

interface RecordingContextValue {
  audioProcessor: AudioProcessorService | null;
  startRecording: (
    appointmentId: string,
    patientId: string,
    locationId: string,
    partNumber: number,
    createdBy: string
  ) => Promise<void>;
  stopRecording: (
    patientId: string,
    loggedInUserId: string,
    shouldCallAPI?: boolean
  ) => Promise<AiScribeCompleteResponse | void>;
  pauseRecording: (patientId: string) => Promise<void>;
  resumeRecording: (patientId: string) => Promise<void>;
  forceCleanup: () => void;
}

const RecordingContext = createContext<RecordingContextValue | undefined>(
  undefined
);

export const useRecordingContext = () => {
  const ctx = useContext(RecordingContext);
  if (!ctx)
    throw new Error(
      'useRecordingContext must be used within a RecordingProvider'
    );
  return ctx;
};

export const RecordingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [audioProcessor, setAudioProcessor] =
    useState<AudioProcessorService | null>(null);
  const recordingServiceRef = useRef<AIScribeRecordingService | null>(null);
  const dispatch = useDispatch();
  // Initialize recording service once
  if (!recordingServiceRef.current) {
    recordingServiceRef.current = new AIScribeRecordingService();
  }

  // Start recording
  const startRecording = useCallback(
    async (
      appointmentId: string,
      patientId: string,
      locationId: string,
      partNumber: number,
      createdBy: string
    ) => {
      if (!recordingServiceRef.current) return;
      await recordingServiceRef.current.startWavRecording(
        appointmentId,
        patientId,
        locationId,
        partNumber,
        createdBy
      );
      setAudioProcessor(recordingServiceRef.current.getAudioProcessor());
    },
    []
  );

  // Stop recording
  const stopRecording = useCallback(
    async (
      patientId: string,
      loggedInUserId: string,
      shouldCallAPI?: boolean
    ): Promise<AiScribeCompleteResponse | void> => {
      if (!recordingServiceRef.current) return;

      const result = await recordingServiceRef.current.stopWavRecording(
        patientId,
        loggedInUserId,
        shouldCallAPI
      );
      setAudioProcessor(null);
      return result;
    },
    []
  );

  // Pause recording
  const pauseRecording = useCallback(async (patientId: string) => {
    if (!recordingServiceRef.current) return;
    await recordingServiceRef.current.pauseWavRecording(patientId);
  }, []);

  // Resume recording
  const resumeRecording = useCallback(async (patientId: string) => {
    if (!recordingServiceRef.current) return;
    await recordingServiceRef.current.resumeWavRecording(patientId);
  }, []);

  // Force cleanup - stops everything immediately
  const forceCleanup = useCallback(() => {
    if (!recordingServiceRef.current) return;
    recordingServiceRef.current.forceCleanup();
    setAudioProcessor(null);
  }, []);

  return (
    <RecordingContext.Provider
      value={{
        audioProcessor,
        startRecording,
        stopRecording,
        pauseRecording,
        resumeRecording,
        forceCleanup,
      }}>
      {children}
    </RecordingContext.Provider>
  );
};
